import { hasFeature, hasPermission } from '../utils/permissions'
import { PERMISSIONS } from '../constants/permissions'
import {
  regionTheme, REGION_INK, REGION_MUTED, REGION_CARD_BG, REGION_SIL_STROKE, REGION_ACTIVE_BG, REGION_HOVER_BG,
} from '../constants/regionTheme'

interface SidebarProps {
  currentPage: string
  userInfo: {
    email: string
    fullName: string
    isAdmin: boolean
    permissions: string[]
    region?: string
  }
  onPageChange: (page: string) => void
}

const menuItems = [
  { id: 'dashboard', feature: 'dashboard', label: '대시보드', icon: 'dashboard', requiredPermission: PERMISSIONS.PAGE_DASHBOARD },
  { id: 'raw-data', feature: 'raw_data', label: '원천 데이터 관리', icon: 'folder', requiredPermission: PERMISSIONS.PAGE_RAW_DATA },
  { id: 'image-explorer', feature: 'image_explorer', label: '이미지 탐색', icon: 'image-search', requiredPermission: PERMISSIONS.PAGE_IMAGE_EXPLORER },
  { id: 'label-data', feature: 'label_data', label: '라벨 데이터 관리', icon: 'tag', requiredPermission: PERMISSIONS.PAGE_LABEL_DATA },
  { id: 'dataset', feature: 'dataset', label: '학습 데이터셋 관리', icon: 'book', requiredPermission: PERMISSIONS.PAGE_DATASET },
  { id: 'test-dataset', feature: 'test_dataset', label: '평가 데이터셋 관리', icon: 'flask', requiredPermission: PERMISSIONS.PAGE_TEST_DATASET },
  { id: 'training', feature: 'training', label: '학습 이력 관리', icon: 'zap', requiredPermission: PERMISSIONS.PAGE_TRAINING },
  { id: 'deployment', feature: 'deployment', label: '모델 배포 관리', icon: 'rocket', requiredPermission: PERMISSIONS.PAGE_DEPLOYMENT },
  { id: 'users', feature: 'users', label: '사용자 관리', icon: 'users', adminOnly: true },
  { id: 'report', feature: 'report', label: '수집 리포트', icon: 'chart', requiredPermission: PERMISSIONS.PAGE_RAW_COLLECTION_REPORT },
]

export default function Sidebar({ currentPage, userInfo, onPageChange }: SidebarProps) {
  // 리전 표시 (kr·us). 없으면(폐쇄망 kr-local 등) 지금까지의 밝은 사이드바 그대로다.
  const theme = regionTheme(userInfo.region)
  // 메뉴 글자색 — [기본, 선택·hover]. 바탕·글자색만 갈리고 모양은 같다.
  const textColor = theme ? 'rgba(255,255,255,0.82)' : '#6b7280'
  const textStrong = theme ? REGION_INK : '#1f2937'
  const hoverColor = theme ? REGION_INK : '#374151'
  const activeBg = theme ? REGION_ACTIVE_BG : '#ebebeb'
  const hoverBg = theme ? REGION_HOVER_BG : '#f9fafb'

  // SVG 아이콘 렌더링 함수
  const renderIcon = (iconName: string) => {
    const iconSize = 20
    const iconColor = theme ? 'currentColor' : '#6b7280'
    
    switch (iconName) {
      case 'dashboard':
        return (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
        )
      case 'folder':
        return (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
            <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.89l-.812-1.22A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path>
          </svg>
        )
      case 'tag':
        return (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
            <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z"></path>
            <circle cx="7" cy="7" r="1"></circle>
          </svg>
        )
      case 'image-search':
        return (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2"></rect>
            <circle cx="9" cy="9" r="2"></circle>
            <path d="m21 15-4-4L5 21"></path>
          </svg>
        )
      case 'book':
        return (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
          </svg>
        )
      case 'zap':
        return (
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
                <path d="M16 7h6v6"/>
                <path d="m22 7-8.5 8.5-5-5L2 17"/>
            </svg>
          
        )
      case 'rocket':
        return (
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
                <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
                <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
                <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
            </svg>
        )
      case 'flask':
        return (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
            <path d="M9 3h6v6l3 9H6L9 9V3z"></path>
            <path d="M6 18h12"></path>
          </svg>
        )
      case 'users':
        return (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        )
      case 'chart':
        return (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2">
            <line x1="18" y1="20" x2="18" y2="10"></line>
            <line x1="12" y1="20" x2="12" y2="4"></line>
            <line x1="6" y1="20" x2="6" y2="14"></line>
            <line x1="2" y1="20" x2="22" y2="20"></line>
          </svg>
        )
      default:
        return null
    }
  }

  return (
    <div style={{
      width: '280px',
      flexShrink: 0, // 메인 컨텐츠가 넓어져도 전역 사이드바 폭은 줄어들지 않도록 고정
      backgroundColor: theme ? theme.bg : '#f8f9fa',
      borderRight: theme ? 'none' : '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '2px 0 4px rgba(0, 0, 0, 0.05)'
    }}>
      {/* 로고 영역 — 리전 색 위에서는 로고가 묻히므로 헤더와 같은 높이의 흰 띠에 둔다 */}
      <div style={theme ? {
        height: '80px',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#fff',
        borderBottom: '1px solid #e5e7eb',
      } : {
        padding: '24px',
        display: 'flex',
      }}>
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }} onClick={() => onPageChange('dashboard')}>
          <div style={{display:'flex', flexDirection:'column'}}>
          <img src="/logo.svg" alt="logo" />
          <span style={{
          fontSize: '14px',
          color: '#9ca3af',
          fontWeight: '500',
          textAlign:"end",
          paddingRight:"5px",
          fontStyle:"oblique"    
        }}>
          MLOps Platform
        </span>
          </div>
        </div>
        
      </div>

      {/* 리전 카드 — 큰 코드 글자 + 지역 실루엣 */}
      {theme && (
        <div style={{
          position: 'relative',
          height: '150px',
          margin: '24px 16px 20px',
          borderRadius: '12px',
          backgroundColor: REGION_CARD_BG,
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          <svg viewBox="0 0 228 150" preserveAspectRatio="xMaxYMid meet"
               style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden="true">
            <path d={theme.path} fill={theme.silFill} stroke={REGION_SIL_STROKE} strokeWidth="0.8" />
          </svg>
          <div style={{ position: 'absolute', left: '16px', bottom: '14px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '44px', fontWeight: 800, lineHeight: 1, letterSpacing: '-1px', color: REGION_INK }}>
              {theme.code}
            </span>
            <span style={{ fontSize: '13px', fontWeight: 600, color: REGION_MUTED }}>{theme.name}</span>
            <span style={{ fontSize: '12px', fontWeight: 500, color: REGION_MUTED }}>{theme.zone}</span>
          </div>
        </div>
      )}

      {/* 메뉴 영역 */}
      <nav style={{ flex: 1 }}>
        {menuItems.map((item) => {
          // 관리자 전용 메뉴는 관리자가 아닐 경우 숨김
          if (item.adminOnly && !userInfo.isAdmin) {
            return null
          }

          // 페이지별 권한이 필요한 메뉴는 권한이 없으면 숨김
          if (item.requiredPermission && !hasPermission(userInfo, item.requiredPermission)) {
            return null
          }

          // 이 배치에서 끈 기능은 숨기지 않고 비활성으로 보인다 — 눌리지 않는다.
          // (주소로 들어오는 건 App 의 canOpen 이, API 는 require_feature 가 막는다)
          if (!hasFeature(userInfo, item.feature)) {
            return (
              <div
                key={item.id}
                title="이 서버에서는 제공하지 않는 기능입니다"
                aria-disabled="true"
                style={{
                  padding: '12px 24px',
                  margin: '4px 16px',
                  borderRadius: '8px',
                  cursor: 'not-allowed',
                  color: textColor,
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: theme ? 0.45 : 0.4,
                }}
              >
                <div style={{ marginRight: '12px' }}>
                  {renderIcon(item.icon)}
                </div>
                {item.label}
              </div>
            )
          }

          const isActive = currentPage === item.id
          
          return (
            <div
              key={item.id}
              style={{
                padding: '12px 24px',
                margin: '4px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: isActive ? activeBg : 'transparent',
                color: isActive ? textStrong : textColor,
                fontWeight: isActive ? '600' : '500',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s ease'
              }}
              onClick={() => onPageChange(item.id)}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = hoverBg
                  e.currentTarget.style.color = hoverColor
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = textColor
                }
              }}
            >
                          <div style={{ marginRight: '12px' }}>
              {renderIcon(item.icon)}
            </div>
              {item.label}
            </div>
          )
        })}
      </nav>
    </div>
  )
}
